import React from 'react'

// 通用版面元件。現場畫面需要大按鍵與高對比（§2.1）。

export function Card({ title, subtitle, right, children, tone = 'plain' }: {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  right?: React.ReactNode
  children?: React.ReactNode
  tone?: 'plain' | 'warn' | 'danger' | 'ok'
}) {
  const border = tone === 'warn' ? 'border-amber-300' : tone === 'danger' ? 'border-red-300'
    : tone === 'ok' ? 'border-emerald-300' : 'border-ink-200'
  const head = tone === 'warn' ? 'bg-amber-50' : tone === 'danger' ? 'bg-red-50'
    : tone === 'ok' ? 'bg-emerald-50' : 'bg-ink-50'
  return (
    <section className={`rounded-lg border ${border} bg-white shadow-sm overflow-hidden`}>
      {(title || right) && (
        <header className={`${head} px-4 py-2.5 border-b ${border} flex items-center justify-between gap-3`}>
          <div className="min-w-0">
            <h2 className="font-semibold text-ink-900 text-[15px] leading-tight truncate">{title}</h2>
            {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
          </div>
          {right && <div className="shrink-0 flex items-center gap-2">{right}</div>}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  )
}

type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
}

export const Button = React.forwardRef<HTMLButtonElement, BtnProps>(function Button(
  { variant = 'secondary', size = 'md', className = '', ...rest }, ref,
) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-sky-500'
  const v = {
    primary: 'bg-sky-700 text-white hover:bg-sky-800 border border-sky-700',
    secondary: 'bg-white text-ink-800 border border-ink-300 hover:bg-ink-50',
    danger: 'bg-red-600 text-white hover:bg-red-700 border border-red-600',
    ghost: 'bg-transparent text-ink-600 hover:bg-ink-100 border border-transparent',
  }[variant]
  const s = {
    sm: 'text-xs px-2.5 py-1.5',
    md: 'text-sm px-3.5 py-2 min-h-[38px]',
    lg: 'text-base px-5 py-3 min-h-touch min-w-touch',
  }[size]
  return <button ref={ref} className={`${base} ${v} ${s} ${className}`} {...rest} />
})

export function Field({ label, hint, required, children, className = '' }: {
  label: React.ReactNode
  hint?: React.ReactNode
  required?: boolean
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-ink-600 mb-1">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-ink-400 mt-1">{hint}</span>}
    </label>
  )
}

/**
 * 與 `Field` 的標籤同高的佔位。
 *
 * 用在「同一列裡既有輸入框也有按鈕」的版面：`Field` 的高度包含**標籤與底下的提示**，
 * 所以用 `items-end` 把按鈕對齊 Field 的底部，實際上是對齊了提示文字的底部，
 * 按鈕會比輸入框低一截；而用 `items-start` 又會比輸入框高出一個標籤的高度。
 *
 * 兩種都常被用「mb-[2px]」這類魔術數字硬湊，但那個數字補不到提示文字的高度，
 * 而且提示一改字數就又跑掉。改成在按鈕上方放一個與標籤同高的佔位，
 * 讓按鈕跟輸入框從同一條線開始——字級或間距以後調整了也不會歪。
 */
export function FieldLabelSpacer() {
  return <span aria-hidden className="block text-xs font-medium mb-1">&nbsp;</span>
}

const inputCls = 'w-full rounded-md border border-ink-300 px-3 py-2 text-sm text-ink-900 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 disabled:bg-ink-100 disabled:text-ink-500'

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { className = '', ...rest } = props
  return <input {...rest} className={`${inputCls} ${className}`} />
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const { className = '', ...rest } = props
  return <select {...rest} className={`${inputCls} ${className}`} />
}

export function Table({ head, children, dense }: {
  head: React.ReactNode[]
  children: React.ReactNode
  dense?: boolean
}) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full min-w-[520px] text-sm border-collapse">
        <thead>
          <tr className="text-left text-xs text-ink-500 border-b border-ink-200">
            {head.map((h, i) => (
              <th key={i} className={`font-medium ${dense ? 'py-1.5' : 'py-2'} pr-3 whitespace-nowrap`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">{children}</tbody>
      </table>
    </div>
  )
}

export function Td({ children, className = '', ...rest }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={`py-2 pr-3 align-middle ${className}`} {...rest}>{children}</td>
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-ink-400 py-6 text-center">{children}</p>
}

export function Alert({ tone = 'warn', title, children }: {
  tone?: 'warn' | 'danger' | 'info' | 'ok'
  title?: React.ReactNode
  children?: React.ReactNode
}) {
  const cls = {
    warn: 'bg-amber-50 border-amber-300 text-amber-900',
    danger: 'bg-red-50 border-red-300 text-red-900',
    info: 'bg-sky-50 border-sky-300 text-sky-900',
    ok: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  }[tone]
  const icon = { warn: '⚠', danger: '✗', info: 'ⓘ', ok: '✓' }[tone]
  return (
    <div className={`rounded-md border px-3 py-2.5 text-sm ${cls}`}>
      <div className="flex gap-2">
        <span aria-hidden className="shrink-0 leading-5">{icon}</span>
        <div className="min-w-0 flex-1">
          {title && <p className="font-semibold leading-5">{title}</p>}
          {children && <div className={`${title ? 'mt-1' : ''} leading-relaxed`}>{children}</div>}
        </div>
      </div>
    </div>
  )
}

export function Pill({ children, tone = 'ink', title }: {
  children: React.ReactNode
  tone?: 'ink' | 'sky' | 'amber' | 'red' | 'emerald' | 'violet'
  title?: string
}) {
  const cls = {
    ink: 'bg-ink-100 text-ink-700 border-ink-200',
    sky: 'bg-sky-100 text-sky-800 border-sky-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-300',
    red: 'bg-red-100 text-red-800 border-red-300',
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    violet: 'bg-violet-100 text-violet-800 border-violet-200',
  }[tone]
  return (
    <span title={title} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${cls}`}>
      {children}
    </span>
  )
}

export function Mono({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <span className={`font-mono text-[12.5px] ${className}`}>{children}</span>
}

export function KV({ items }: { items: [React.ReactNode, React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
      {items.map(([k, v], i) => (
        <React.Fragment key={i}>
          <dt className="text-ink-500 whitespace-nowrap">{k}</dt>
          <dd className="text-ink-900">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  )
}

export function Steps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-1.5 text-xs mb-4">
      {steps.map((s, i) => {
        const done = i < current
        const active = i === current
        return (
          <li key={s} className="flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${
              active ? 'bg-sky-700 text-white border-sky-700 font-medium'
                : done ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                  : 'bg-white text-ink-400 border-ink-200'}`}
            >
              <span className="tabular-nums">{done ? '✓' : i + 1}</span>{s}
            </span>
            {i < steps.length - 1 && <span className="text-ink-300">›</span>}
          </li>
        )
      })}
    </ol>
  )
}

export function Section({ title, children, right }: {
  title: React.ReactNode; children: React.ReactNode; right?: React.ReactNode
}) {
  return (
    <div className="mb-3">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="text-xs font-semibold text-ink-500 uppercase tracking-wide">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  )
}
